import { createTheme } from "@mui/material/styles";

export type AppThemeName = "light" | "white" | "dark" | "blue";

export const DEFAULT_APP_THEME: AppThemeName = "light";

export const APP_THEME_OPTIONS: Array<{ label: string; value: AppThemeName }> = [
  { label: "Бежевая", value: "light" },
  { label: "Светлая", value: "white" },
  { label: "Темная", value: "dark" },
  { label: "Синяя", value: "blue" },
];

export const normalizeAppThemeName = (theme?: string | null): AppThemeName => {
  if (theme === "white" || theme === "dark" || theme === "blue" || theme === "light") return theme;
  return DEFAULT_APP_THEME;
};

export const appThemeTokens = {
  light: {
    mode: "light",
    primary: "#827c64",
    primaryHover: "#6f694f",
    primaryLight: "#dcd8cc",
    success: "#16A34A",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
    background: "#faf9f5",
    surface: "#ffffff",
    border: "#dcd8cc",
    divider: "#dcd8cc",
    hover: "#dcd8cc",
    disabled: "#eeeeee",
    textPrimary: "#616161",
    textSecondary: "#535353",
    textMuted: "#827c64",
    icon: "#616161",
  },
  white: {
    mode: "light",
    primary: "#0EA5C8",
    primaryHover: "#0B91B2",
    primaryLight: "#E0F2FE",
    success: "#16A34A",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
    background: "#F7FAFC",
    surface: "#FFFFFF",
    border: "#E2E8F0",
    divider: "#EDF2F7",
    hover: "#F1F5F9",
    disabled: "#F8FAFC",
    textPrimary: "#0F172A",
    textSecondary: "#334155",
    textMuted: "#64748B",
    icon: "#334155",
  },
  dark: {
    mode: "dark",
    primary: "#0EA5C8",
    primaryHover: "#0B91B2",
    primaryLight: "#164E63",
    success: "#22C55E",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
    background: "#0B1220",
    surface: "#111827",
    border: "#1F2937",
    divider: "#334155",
    hover: "#1F2937",
    disabled: "#334155",
    textPrimary: "#E2E8F0",
    textSecondary: "#CBD5E1",
    textMuted: "#94A3B8",
    icon: "#CBD5E1",
  },
  blue: {
    mode: "light",
    primary: "#628eb8",
    primaryHover: "#4f7aa5",
    primaryLight: "#e9f2ff",
    success: "#16A34A",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
    background: "#f8f8f8",
    surface: "#ffffff",
    border: "#dfe9f6",
    divider: "#dfe9f6",
    hover: "#e9f2ff",
    disabled: "#eeeeee",
    textPrimary: "#17264a",
    textSecondary: "#334155",
    textMuted: "#64748B",
    icon: "#ffffff",
  },
} as const satisfies Record<AppThemeName, { mode: "light" | "dark"; [key: string]: string }>;

export const getAppThemeTokens = (theme: string) => appThemeTokens[normalizeAppThemeName(theme)];

export const createMuiTheme = (theme: string) => {
  const tokens = getAppThemeTokens(theme);

  return createTheme({
    palette: {
      mode: tokens.mode,
      primary: {
        main: tokens.primary,
        light: tokens.primaryLight,
        dark: tokens.primaryHover,
      },
      success: {
        main: tokens.success,
      },
      warning: {
        main: tokens.warning,
      },
      error: {
        main: tokens.error,
      },
      info: {
        main: tokens.info,
      },
      background: {
        default: tokens.background,
        paper: tokens.surface,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      divider: tokens.divider,
    },
    components: {
      MuiBadge: {
        styleOverrides: {
          badge: {
            backgroundColor: tokens.primary,
          },
        },
      },
    },
  });
};
